/**
 * Whether the name typed on a room application should be written onto the
 * signed-in applicant's profile.
 *
 * It matters because every admin screen reads `profiles.full_name` before
 * `bookings.name` (tenant profile, room's current tenant, support launcher), so
 * a name that stays on the booking alone is never seen. A signed-in applicant
 * used to leave the profile untouched, and an account that held only a first
 * name kept showing only that.
 *
 * Deliberately narrow: the profile is also what an admin corrects by hand and,
 * for an owner's account, what the owner record mirrors (trigger
 * `profiles_sync_to_owner_record`). So a name already settled on is never replaced by
 * whatever was typed here. Only a gap is filled: no name yet, or a name that is
 * the first words of what was typed ("Amira" -> "Amira Haddad").
 */
export function profileNameFromApplication(
  current: { name?: string | null; full_name?: string | null } | null,
  typed: string
): string | null {
  const wanted = typed.trim().replace(/\s+/g, " ");
  if (!wanted) return null;

  const held = (current?.full_name || current?.name || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!held) return wanted;

  const heldWords = held.toLowerCase().split(" ");
  const wantedWords = wanted.toLowerCase().split(" ");
  const extendsHeld =
    wantedWords.length > heldWords.length &&
    heldWords.every((word, i) => word === wantedWords[i]);
  return extendsHeld ? wanted : null;
}
